/**
 * Iterates over the keys of an object and applies a given action to each key-value pair.
 *
 * @param obj - The Object
 * @param action - The function
 *
 * @example
 * ```typescript
 * // To execute a function against every value in an object i.e. console logging each key/value pair
 * forEachKey(
 *   {'test1': 'test1', 'test2': 'test2', 'test3': 'test3'},
 *   (value, key) => {
 *     console.log(`Key: ${key}, Value: ${value}`);
 *   }
 * );
 *
 * ```
 */
export function forEachKey(obj, action) {
    for (const index in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, index)) {
            action(obj[index], index, obj);
        }
    }
}
export function findKey(obj, predicate) {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && predicate(obj[key], key, obj)) {
            return { key, value: obj[key] };
        }
    }
    return undefined;
}
export function mapKeys(obj, iteratee) {
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[key] = iteratee(obj[key], key, obj);
        }
    }
    return result;
}
/**
 * Retrieves the value of a deeply nested property from an object.
 *
 * @param {Object} obj - The object to retrieve the property value from.
 * @param {string} path - The dot-separated path of the property.
 *
 * @return {*} - The value of the property at the given path. If the path is not valid or the property does not exist, it returns undefined.
 */
export function getDeepProperty(obj, path) {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}
// returns true they match.
// this is a shallow diff of primitive typed values only, not array or object property values. can implement object/array support if needed
export function shallowDiff(a, b) {
    if (!a && b) {
        return false;
    }
    if (!b && a) {
        return false;
    }
    if (!a && !b) {
        return true;
    }
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) {
        return false;
    }
    for (const key of aKeys) {
        if (!(key in b)) {
            return false;
        }
        const valA = a[key];
        const valB = b[key];
        if (valA !== valB) {
            return false;
        }
    }
    return true;
}
/**
 * Parse an XML string and return an object
 *
 * @param xmlStr - e.g. "<wrapper>\r\n\t<sessionid>GC1138068</sessionid>\r\n</wrapper>"
 * @returns an object like { sessionid: "GC1138068" }
 */
export function parseXml(xmlString) {
    const parser = new DOMParser;
    // strip any new-line, tab, return characters
    xmlString = xmlString.replace(/[\r\n\t\v\f\u2028\u2029]+/g, '');
    const xmlDoc = parser.parseFromString(xmlString.trim(), "application/xml");
    function xmlToJson(node) {
        // If it’s a text node, return a basic object with the value
        if (node.nodeType === Node.TEXT_NODE) {
            return (node.nodeValue ?? "").trim();
        }
        // If it’s an element, look at its children (excluding pure-whitespace text)
        if (node instanceof Element) {
            const meaningfulChildren = Array.from(node.childNodes).filter((ch) => !(ch.nodeType === Node.TEXT_NODE && (ch.nodeValue ?? "").trim() === ""));
            // 1) No children → treat as empty string
            if (meaningfulChildren.length === 0) {
                return "";
            }
            // 2) Exactly one child and it’s a text node → return that text
            if (meaningfulChildren.length === 1 &&
                meaningfulChildren[0].nodeType === Node.TEXT_NODE) {
                return (meaningfulChildren[0].nodeValue ?? "").trim();
            }
            // Otherwise fall through to treat it as an object with sub-elements
        }
        const result = {};
        // 4.b) Process child nodes
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE &&
                (child.nodeValue ?? "").trim() === "") {
                return;
            }
            const name = child.nodeName;
            const value = xmlToJson(child);
            // If the text content is empty (e.g. <detail></detail>), store "" directly
            if (value === "") {
                result[name] = "";
                return;
            }
            // If we haven’t seen this tag before, assign it
            if (result[name] === undefined) {
                result[name] = value;
            }
            else {
                // If it’s already an array, push the new value; otherwise convert to an array
                if (Array.isArray(result[name])) {
                    result[name].push(value);
                }
                else {
                    result[name] = [result[name], value];
                }
            }
        });
        return result;
    }
    return xmlToJson(xmlDoc.documentElement);
}
/**
 * deepClone(value)
 *
 * Creates a deep copy of `value`. Supports nested objects, arrays, Date, RegExp,
 * Map, and Set. Functions and other non‐plain-object instances are returned by
 * reference (i.e. not cloned).
 *
 * @param {*} value  – anything (primitive, object, array, Date, RegExp, Map, Set, etc.)
 * @returns {*} a deep copy of `value`
 */
export function deepClone(value) {
    // 1) Primitives or functions: return as-is
    if (value === null || typeof value !== 'object') {
        return value;
    }
    // 2) Date → new Date(...)
    if (value instanceof Date) {
        return new Date(value.getTime());
    }
    // 3) RegExp → new RegExp(...)
    if (value instanceof RegExp) {
        // copy flags and source
        const reFlags = value.flags; // e.g. "gi"
        return new RegExp(value.source, reFlags);
    }
    // 4) Array → map each element
    if (Array.isArray(value)) {
        const arrCopy = [];
        for (let i = 0; i < value.length; i++) {
            arrCopy[i] = deepClone(value[i]);
        }
        return arrCopy;
    }
    // 5) Map → clone each [key, val] pair
    if (value instanceof Map) {
        const mapCopy = new Map();
        value.forEach((val, key) => {
            // Note: if you want to deep‐clone keys as well, do: deepClone(key)
            mapCopy.set(key, deepClone(val));
        });
        return mapCopy;
    }
    // 6) Set → clone each value
    if (value instanceof Set) {
        const setCopy = new Set();
        value.forEach((val) => {
            setCopy.add(deepClone(val));
        });
        return setCopy;
    }
    // 7) Plain object → clone each own property
    //    (if it’s some other object type—e.g. custom class instance—this will
    //    create a shallow “{ }” and copy only enumerable own properties)
    if (Object.prototype.toString.call(value) === '[object Object]') {
        const objCopy = {};
        for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
                objCopy[key] = deepClone(value[key]);
            }
        }
        return objCopy;
    }
    // 8) Anything else (e.g. functions, DOM nodes, class instances, etc.)
    //    – just return the original reference
    return value;
}
